import type React from 'react';
import type * as yup from 'yup';
import type {
  CompDefinition,
  CompInfo,
  DefaultCompPlatformProps,
} from '@wix/editor-elements-types/thunderbolt';
import type {
  LoginSocialBarSdkData,
  ResolvedCustomMenu,
} from '@wix/thunderbolt-components-native';
import type { LinkProps } from '@wix/editor-elements-definitions';
import type { CorvidTypes } from '@wix/editor-elements-types/corvid';
import type {
  IElementPropsSDK,
  IElementPropsSDKActions,
} from '@wix/editor-elements-corvid-utils';
import type { PickStateRefValues } from '@wix/editor-elements-integrations';
import type { Overwrite } from '@wix/editor-elements-types/utils';
import type { MapperReportClickProps } from '@wix/editor-elements-common-utils';
import type { ViewerTranslations } from '@wix/editor-elements-types/components';
import type {
  dataSchema,
  propertiesSchema,
} from './documentManagement/LoginSocialBar.schemaTypes';
import type { getCssMapperOutput } from './viewer/mappers';
import type { getLoginBarStyleValues } from './utils';

export enum AvatarState {
  Initials,
  Image,
  Vector,
}

export enum Layout {
  AvatarOnly = 'avatarOnly',
  AvatarAndText = 'avatarAndText',
  TextOnly = 'textOnly',
  /**
   * @deprecated initials moved to AvatarIcon
   */
  MemberInitials = 'memberInitials',
}

export enum LoginBarState {
  LoggedIn = 'loggedIn',
  LoggedOut = 'loggedOut',
  LoggedInMenuOpened = 'loggedInMenuOpened',
}

export enum LoggedInTextState {
  MemberName = 'memberName',
  Custom = 'custom',
}

export enum AvatarIcon {
  ProfileImage = 'profileImage',
  Initials = 'initials',
  Custom = 'custom',
}

export enum ArrowShape {
  None = 'none',
  Line = 'line',
  Full = 'full',
}

export enum Alignment {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export enum Direction {
  LTR = 'ltr',
  RTL = 'rtl',
}

export interface ILoginSocialBarCustomMenu {
  focusFirstMenuItem(): void;
}

export type LoginSocialBarUnresolvedData = yup.InferType<typeof dataSchema>; // TODO: https://wix.atlassian.net/browse/MA-4496
export type LoginSocialBarData = Overwrite<
  LoginSocialBarUnresolvedData,
  {
    menuItemsRef: { menuRef: ResolvedCustomMenu };
    iconItemsRef: { menuRef: ResolvedCustomMenu };
  }
>;
export type LoginSocialBarProperties = yup.InferType<typeof propertiesSchema>; // TODO: https://wix.atlassian.net/browse/MA-4496
export type LoginSocialBarSkin = 'LoginSocialBarSkin';

export type LoginSocialBarDefinition = CompDefinition<
  LoginSocialBarSkin,
  LoginSocialBarData,
  LoginSocialBarProperties,
  never,
  LoginSocialBarUnresolvedData
>;

export type StyleProperties = CompInfo['styleProperties'];

export type LoginBarStyleValues = ReturnType<typeof getLoginBarStyleValues>;

export type CarmiCss = {
  '--fnt': string;
  'justify-content': CSSStyleDeclaration['justifyContent'];
  direction: CSSStyleDeclaration['direction'];
  '--icon-size': string;
};

export type LoginSocialBarCSSVars = ReturnType<typeof getCssMapperOutput>;

export type AvatarWrapperProps = {
  customAvatar: string;
  avatarIcon: AvatarIcon;
  avatarUri?: string;
  isLoggedIn?: ILoginSocialBarProps['isLoggedIn'];
  userName?: ILoginSocialBarProps['userName'];
} & Pick<ILoginSocialBarProps, 'id' | 'useNewMemberInitialsAvatar'>;

export type LoginSocialBarMenuItemProps = {
  label: string;
  link: LinkProps;
  displayCount?: number;
};

export type LoginSocialBarIconItemProps = LoginSocialBarMenuItemProps & {
  iconSvgContent: string;
};

export type LoginSocialBarSiteAssetProps = {
  /**
   * Skin name.
   */
  skin: LoginSocialBarDefinition['skin'];
  /**
   * Display mode loggedIn for login bar.
   */
  displayMode: LoginSocialBarData['loggedInMember'];
  /**
   * avatarIcon mode for loggedIn login bar.
   */
  loggedInAvatarIcon: LoginSocialBarData['loggedInAvatarIcon'];
  /**
   * custom avatar link for loggedIn login bar.
   */
  loggedInCustomAvatar: LoginSocialBarData['loggedInCustomAvatar'];
  /**
   * avatarIcon mode for loggedOut login bar.
   */
  loggedOutAvatarIcon: LoginSocialBarData['loggedOutAvatarIcon'];
  /**
   * custom avatar link for loggedOut login bar.
   */
  loggedOutCustomAvatar: LoginSocialBarData['loggedOutCustomAvatar'];
  /**
   * Text for login message.
   */
  loggedOutText: LoginSocialBarData['loggedOutText'];
  /**
   * Text for logout message.
   */
  logOutText: LoginSocialBarData['logOutText'];
  /**
   * Display Logged In Text with member name or custom
   */
  loggedInTextState: LoginSocialBarData['loggedInTextState'];
  /**
   * Greeting text
   */
  loggedInText: LoginSocialBarData['loggedInText'];
  /**
   * Custom text to display instead of user name
   */
  customLoggedInText: LoginSocialBarData['customLoggedInText'];
  /**
   * Arrangement of icons.
   */
  direction: LoginSocialBarProperties['buttonsDirection'];
  /**
   * Use native instead of custom HTML menu.
   */
  useNativeMenu: boolean;
  /**
   * Menu items for dropdown menu.
   */
  menuItems: Array<LoginSocialBarMenuItemProps>;
  /**
   * Icon items to be displayed on login bar.
   */
  iconItems: Array<LoginSocialBarIconItemProps>;
  /**
   * Default avatar SVG content.
   */
  defaultAvatarSvgContent: string;

  enableLoggingInStateInSSR: boolean;

  useNewMemberInitialsAvatar: boolean;
};

export type LoginSocialBarFeatureProps = {
  /**
   * The href of the current primary page.
   */
  currentPrimaryPageHref: string;
  /**
   * Whether the user is logged in or not.
   */
  isLoggedIn: boolean;
  /**
   * Logged in user name.
   */
  userName: string;
  /**
   * Avatar image URI for logged in user.
   */
  avatarUri: string;
  /**
   * Event used to trigger login action.
   */
  onLogin(): void;
  /**
   * Event used to trigger logout action.
   */
  onLogout(event?: React.MouseEvent): void;
};

export type ILoginSocialBarMapperProps = LoginSocialBarSiteAssetProps &
  LoginSocialBarFeatureProps &
  Omit<MapperReportClickProps, 'pagesMap'> &
  Pick<DefaultCompPlatformProps, 'fullNameCompType'>;

export type ILoginSocialBarStateValues = PickStateRefValues<
  | 'memberDetails'
  | 'reportBi'
  | 'currentUrl'
  | 'navigateTo'
  | 'logout'
  | 'promptLogin'
>;

export type ILoginSocialBarControllerProps = Omit<
  ILoginSocialBarMapperProps,
  keyof MapperReportClickProps
> &
  Pick<ILoginSocialBarStateValues, 'navigateTo' | 'reportBi'> & {
    onMenuOpen(): void;
    onMenuClose(): void;
    reportBiOnClick: (label: string) => void;
    reportBiOnMenuItemClick: (item: LoginSocialBarMenuItemProps) => void;
  };

export type ILoginSocialBarPreviewMapperProps = ILoginSocialBarMapperProps & {
  isMenuOpen: boolean;
};

export type ILoginSocialBarProps = DefaultCompPlatformProps &
  ILoginSocialBarControllerProps &
  Partial<IElementPropsSDKActions> & {
    isMenuOpen: boolean;
    className?: string;
    onBlur?: React.FocusEventHandler;
  } & ViewerTranslations<
    | 'loggedOutText'
    | 'logOutText'
    | 'menuOptionValueText'
    | 'accountButtonAriaLabel'
  >;

type ILoginSocialBarSDKDataMenuItem = LoginSocialBarSdkData['menuItems'][0];
export type ILoginSocialBarSDKMenuItem =
  Partial<ILoginSocialBarSDKDataMenuItem> &
    Pick<ILoginSocialBarSDKDataMenuItem, 'label'>;

export type ILoginSocialBarSDKMenuItems = Array<ILoginSocialBarSDKMenuItem>;

type ILoginSocialBarSDKDataNavBarItem = LoginSocialBarSdkData['navBarItems'][0];
export type ILoginSocialBarSDKNavBarItem =
  Partial<ILoginSocialBarSDKDataNavBarItem> &
    Pick<ILoginSocialBarSDKDataNavBarItem, 'icon'>;

export type ILoginSocialBarSDKNavBarItems = Array<ILoginSocialBarSDKNavBarItem>;

export type ILoginSocialBarOwnSDK = {
  menuItems: ILoginSocialBarSDKMenuItems;
  navBarItems: ILoginSocialBarSDKNavBarItems;
};

export type ILoginSocialBarSDK = ILoginSocialBarOwnSDK & IElementPropsSDK;

export type ILoginSocialBarOwnSDKFactory = CorvidTypes.CorvidSDKFactory<
  ILoginSocialBarProps,
  ILoginSocialBarOwnSDK,
  LoginSocialBarSdkData
>;

export type ILoginSocialBarSDKFactory = CorvidTypes.CorvidSDKFactory<
  ILoginSocialBarProps,
  ILoginSocialBarSDK,
  LoginSocialBarSdkData
>;

export type ILoginSocialBarCorvidModel =
  CorvidTypes.ICorvidModel<ILoginSocialBarSDKFactory>;
